export type IPrimarySystemKind =
  | "SUPPLY_AIR"
  | "EXHAUST_AIR"
  | "HEATING"
  | "COOLING"
  | "COLD_WATER"
  | "HOT_WATER"
  | "VVC"
  | "DRAINAGE"
  | "STORMWATER"
  | "FIRE_PROTECTION"
  | "MEDICAL_GAS"
  | "COMPRESSED_AIR"
  | "TRANSPORT_LOGISTICS"
  | "FUEL"
  | "WATER_TREATMENT"
  | "RADON"
  | "OTHER";

export interface IPrimarySystemSpec {
  label: string; // UI label
  color: string; // hex
  sortOrder: number;
}
export const PRIMARY_SYSTEM_SPECS: Record<IPrimarySystemKind, IPrimarySystemSpec> = {
  SUPPLY_AIR: {
    label: "Tilluft",
    color: "#0080c0", // matchar DeviceCategoryEnum.SUPPLY_AIR_DEVICE
    sortOrder: 10,
  },
  EXHAUST_AIR: {
    label: "Avtrekk",
    color: "#d2d248", // matchar DeviceCategoryEnum.RETURN_AIR_DEVICE
    sortOrder: 20,
  },

  HEATING: {
    label: "Varme",
    color: "#F77F00",
    sortOrder: 30,
  },
  COOLING: {
    label: "Kjøling",
    color: "#00B4D8",
    sortOrder: 40,
  },

  COLD_WATER: {
    label: "Kaldtvann (KV)",
    color: "#90E0EF",
    sortOrder: 50,
  },
  HOT_WATER: {
    label: "Varmtvann (VV)",
    color: "#FFB703",
    sortOrder: 60,
  },
  VVC: {
    label: "VVC",
    color: "#FB8500",
    sortOrder: 70,
  },

  DRAINAGE: {
    label: "Spillvann / avløp",
    color: "#6C757D",
    sortOrder: 80,
  },
  STORMWATER: {
    label: "Overvann",
    color: "#00A896",
    sortOrder: 90,
  },

  FIRE_PROTECTION: {
    label: "Brannslokking",
    color: "#FF0000", // matchar DeviceCategoryEnum.FIRE_SUPPRESSION_DEVICE
    sortOrder: 100,
  },

  MEDICAL_GAS: {
    label: "Medisinske gasser",
    color: "#7209B7",
    sortOrder: 110,
  },
  COMPRESSED_AIR: {
    label: "Trykkluft",
    color: "#3A86FF",
    sortOrder: 120,
  },

  TRANSPORT_LOGISTICS: {
    label: "Transport / logistikk",
    color: "#8338EC",
    sortOrder: 130,
  },

  FUEL: {
    label: "Drivstoff",
    color: "#8D6E63",
    sortOrder: 140,
  },

  WATER_TREATMENT: {
    label: "Vannbehandling",
    color: "#2A9D8F",
    sortOrder: 150,
  },

  RADON: {
    label: "Radon",
    color: "#2D3142",
    sortOrder: 160,
  },

  OTHER: {
    label: "Annet / ukjent",
    color: "#ADB5BD",
    sortOrder: 999,
  },
};

// ---- super snabb normalisering ----
const normalizeFast = (s: string): string => {
  // keep it cheap: lowercase + replace common separators -> space + collapse spaces
  return s
    .toLowerCase()
    .replace(/[åä]/g, "a")
    .replace(/ö/g, "o")
    .replace(/[_/.\-:()]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

// ---- precompiled regexes, ordered by priority ----
const RE = {
  // tappvatten split
  VVC: /\bvvc\b|\bsirk(ulasjon)?\b|\bcirk(ulation)?\b/,
  HOT_WATER: /\bvarmvatten\b|\bhot water\b|\bdhw\b|\bvv\b|\bvarmtvann\b/,
  COLD_WATER: /\bkallvatten\b|\bcold water\b|\bcw\b|\bkv\b|\bkaldtvann\b/,

  // ventilation
  SUPPLY_AIR: /\btilluft\b|\bsupply\b|\bsupply air\b|\bsa\b|\btilf\b/,
  EXHAUST_AIR: /\bfranluft\b|\bfrå(n)?luft\b|\bavtrekk\b|\bexhaust\b|\bea\b|\bavk(ast)?\b/,

  // varme / kyla
  HEATING:
    /\bvarme\b|\bheating\b|\bfjernvarme\b|\bradiator\b|\bgulvvarme\b|\bsnosmelt\b|\bsnøsmelt\b/,
  COOLING: /\bkyla\b|\bkjol\b|\bkjøling\b|\bcooling\b|\bchilled\b|\bisvann\b/,

  // avlop / overvann
  DRAINAGE: /\bspillvann\b|\bavlop\b|\bavlopp\b|\bsoil\b|\bwaste\b|\bfettutskiller\b/,
  STORMWATER: /\bovervann\b|\bdagvatten\b|\bstorm\b|\brain\b/,

  // brann
  FIRE_PROTECTION: /\bsprinkler\b|\bbrann\b|\bbrannvann\b|\bstigeledning\b|\bfoam\b|\bskum\b/,

  // gass / trykkluft
  MEDICAL_GAS:
    /\bmedisinsk\b|\boxygen\b|\bo2\b|\bn2o\b|\blystgass\b|\bco2\b|\bnitrogen\b|\bargon\b/,
  COMPRESSED_AIR: /\btrykkluft\b|\btrykkluft\b|\bcompressed air\b|\binstrumentluft\b/,

  // transport/logistikk
  TRANSPORT_LOGISTICS: /\brorpost\b|\brørpost\b|\bavfallsug\b/,

  // drivstoff
  FUEL: /\bfuel\b|\bdrivstoff\b/,

  // vannbehandling
  WATER_TREATMENT: /\bro vann\b|\brovann\b|\bwater treatment\b/,

  // radon
  RADON: /\bradon\b/,
} as const;

// ---- cache per unique system name ----
const primarySystemCache = new Map<string, IPrimarySystemKind>();

export const inferPrimarySystemKindFromPrimarySystemName = (
  primarySystemName: string | null | undefined
): IPrimarySystemKind => {
  if (!primarySystemName) return "OTHER";

  const cached = primarySystemCache.get(primarySystemName);
  if (cached) return cached;

  const s = normalizeFast(primarySystemName);

  // Priority order matters (VVC must win vs VV, etc.)
  let kind: IPrimarySystemKind = "OTHER";

  if (RE.VVC.test(s)) kind = "VVC";
  else if (RE.HOT_WATER.test(s)) kind = "HOT_WATER";
  else if (RE.COLD_WATER.test(s)) kind = "COLD_WATER";
  else if (RE.SUPPLY_AIR.test(s)) kind = "SUPPLY_AIR";
  else if (RE.EXHAUST_AIR.test(s)) kind = "EXHAUST_AIR";
  else if (RE.HEATING.test(s)) kind = "HEATING";
  else if (RE.COOLING.test(s)) kind = "COOLING";
  else if (RE.DRAINAGE.test(s)) kind = "DRAINAGE";
  else if (RE.STORMWATER.test(s)) kind = "STORMWATER";
  else if (RE.FIRE_PROTECTION.test(s)) kind = "FIRE_PROTECTION";
  else if (RE.MEDICAL_GAS.test(s)) kind = "MEDICAL_GAS";
  else if (RE.COMPRESSED_AIR.test(s)) kind = "COMPRESSED_AIR";
  else if (RE.TRANSPORT_LOGISTICS.test(s)) kind = "TRANSPORT_LOGISTICS";
  else if (RE.FUEL.test(s)) kind = "FUEL";
  else if (RE.WATER_TREATMENT.test(s)) kind = "WATER_TREATMENT";
  else if (RE.RADON.test(s)) kind = "RADON";

  primarySystemCache.set(primarySystemName, kind);
  return kind;
};

// (valfritt) om du vill kunna nolla cache vid hot reload/dev
export const clearPrimarySystemCache = () => primarySystemCache.clear();
